import { useCallback, useEffect, useRef, useState } from "react";
import { Application, Category, TimeTracker, ServiceCase } from "./trackConfig";
import { useGet } from "restful-react";
import { useGetOktaTokenWithoutRefresh } from "../../util/getOktaToken";
import { convertHealthPlanNameToEnum } from "../../util/healthPlanUtils";
import { setLauncherTrackerUniqueIdentifier } from "./launcherContext";
import isEqual from "lodash/isEqual";

export interface TrackRouteProps {
  baseUrl: string;
  application: Application;
  component: string;
  category: Category;
  caseId?: string | null;
  serviceRequestId?: string;
  queueId?: string;
  reviewId?: string;
  // Cohere Launcher (RW-747)
  source?: Application;
  healthPlanName?: string;
  delegatedVendorName?: string;
  // RW-748: for launcher review routes, hold the POST until reviewId is present (created at start
  // action and synced to the URL) so the tracker is created once already carrying reviewId.
  launcherRequiresReviewId?: boolean;
  // RW-747: the enableTimeTracker decision, evaluated on the landing page (inside the providers)
  // and passed down. Undefined for non-launcher trackers (AUTH/QM are never flag-gated).
  trackingEnabled?: boolean;
}

export interface TimeTrackerCaseData {
  application: Application;
  component: string;
  category: Category;
  caseId?: string | null;
  queueId?: string;
  reviewId?: string;
  // Cohere Launcher fields (RW-747). Omitted for AUTH/QM.
  source?: Application;
  serviceRequestId?: string;
  healthPlan?: string;
  delegatedVendor?: string;
}

export default function TrackRoute({
  baseUrl,
  application,
  component,
  category,
  caseId,
  serviceRequestId,
  queueId,
  reviewId,
  source,
  healthPlanName,
  delegatedVendorName,
  launcherRequiresReviewId,
  trackingEnabled: launcherTrackingEnabled,
}: TrackRouteProps) {
  const endpoint = baseUrl + "timeTracker";
  const intervalId = useRef<NodeJS.Timer>();
  const token = useGetOktaTokenWithoutRefresh();
  const validToken = !!token;
  const serviceCaseId = useRef<string | undefined | null>(caseId);
  const [queueIdRef, setQueueId] = useState<string | undefined>(queueId);

  const isLauncher = application === "COHERE_LAUNCHER";

  // RW-747: gate ALL launcher FE->backend calls behind enableTimeTracker. The flag is evaluated on
  // the landing page (inside the RestfulProvider/LaunchDarkly providers) and passed down as
  // launcherTrackingEnabled — TrackRoute renders outside those providers and cannot evaluate it
  // itself. The flag is launcher-only, so trackingEnabled is always true for non-launcher trackers.
  const trackingEnabled = !isLauncher || Boolean(launcherTrackingEnabled);

  // Track the data we use to start a time tracker, and avoid starting
  // multiple time trackers for the same case/data. This was happening
  // when the Okta token expires and then is refreshed from another
  // part of the app, re-triggering the useEffect that starts the time
  // tracker.
  const [lastCreatedTrackerData, setLastCreatedTrackerData] = useState<TimeTrackerCaseData | null>(null);

  const { refetch: searchServiceCases } = useGet<ServiceCase[]>(baseUrl + "serviceCase/search", {
    requestOptions: {
      method: "POST",
      body: JSON.stringify({
        serviceRequestId: serviceRequestId,
        caseType: "P2P_REVIEW",
        caseStatusList: ["OPEN", "IN_PROGRESS"],
      }),
      headers: { "Content-Type": "application/json", authorization: `Bearer ${token}` },
    },
    lazy: true,
  });

  const timeTrackerCaseData: TimeTrackerCaseData = isLauncher
    ? {
        // Launcher trackers are case-agnostic: send source + serviceRequestId (+ reviewId/tenancy),
        // omit caseId/queueId. uniqueIdentifier is generated server-side (RW-744).
        application,
        category,
        component,
        source: source ?? "COHERE_LAUNCHER",
        serviceRequestId,
        reviewId,
        // qm-service tenancy is keyed on health-plan CODEs (e.g. ESSENCE_HEALTHCARE), but the SR
        // carries the display name (e.g. "Essence Healthcare"). Convert so the pre-persist tenancy
        // check passes; likewise the delegated vendor.
        healthPlan: healthPlanName ? convertHealthPlanNameToEnum(healthPlanName) : undefined,
        delegatedVendor: delegatedVendorName ? convertHealthPlanNameToEnum(delegatedVendorName) : undefined,
      }
    : {
        application,
        category,
        component,
        caseId: serviceCaseId.current,
        queueId: queueIdRef,
        reviewId,
      };

  const {
    data,
    error: startTrackingError,
    refetch: startTracking,
  } = useGet<TimeTracker>(endpoint, {
    requestOptions: {
      method: "POST",
      body: JSON.stringify(timeTrackerCaseData),
      headers: { "Content-Type": "application/json", authorization: `Bearer ${token}` },
    },
    lazy: true,
  });

  const { error: keepAliveError, refetch: keepAlive } = useGet<TimeTracker>(
    endpoint + "/" + (data?.id || "") + "/keepAlive",
    {
      requestOptions: {
        method: "PATCH",
        headers: { authorization: `Bearer ${token}` },
        keepalive: true,
      },
      lazy: true,
    }
  );

  // Auth token must be sent through query params because sendBeacons does not allow auth headers
  const stop = useCallback(
    async (stoppedReason?: string) => {
      // RW-747: the stop beacon is gated for launcher trackers too (flag off => no stop call).
      if (!trackingEnabled) {
        return false;
      }
      const queryParams = `stoppedReason=${stoppedReason || "PAGE_EXIT"}`;
      const url = `${endpoint}/${data?.id || ""}/stop?${queryParams}`;
      const request = JSON.stringify({ authorization: `Bearer ${token}` });
      const blob = new Blob([request], { type: "application/json;charset=UTF-8" });

      return navigator.sendBeacon(url, blob);
    },
    [data?.id, endpoint, token, trackingEnabled]
  );

  useEffect(() => {
    const handlePageHide = async () => {
      try {
        if (data?.id) {
          await stop();
        }
      } catch (error: any) {
        console.error("Failed to execute callback for useOnUnload.", error);
      }
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => document.removeEventListener("pagehide", handlePageHide);
    // We only want to run this hook when the "data.id" changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  // RW-748: once a launcher tracker is created, retain its server-generated uniqueIdentifier on
  // the launcher session context so a later completion step (RW-749) can PATCH attachIdentifiers.
  useEffect(() => {
    if (isLauncher && data?.uniqueIdentifier) {
      setLauncherTrackerUniqueIdentifier(data.uniqueIdentifier);
    }
  }, [isLauncher, data?.uniqueIdentifier]);

  useEffect(() => {
    // RW-747: launcher start is gated by enableTimeTracker; flag off => no POST /timeTracker.
    if (validToken && trackingEnabled) {
      if (isLauncher) {
        // Launcher has no ServiceCase — do NOT call serviceCase/search; start directly.
        // For review routes (launcherRequiresReviewId), wait until reviewId is present (created at
        // start action and synced to the URL) so the tracker is created once already carrying it —
        // avoids a second POST when reviewId later appears. Note/letter/outreach have no reviewId
        // and start immediately.
        const waitingForReviewId = !!launcherRequiresReviewId && !reviewId;
        if (!waitingForReviewId && !isEqual(timeTrackerCaseData, lastCreatedTrackerData)) {
          startTracking().then(() => {
            setLastCreatedTrackerData(timeTrackerCaseData);
          });
        }
      } else if (!caseId && component === "Clinical Review") {
        searchServiceCases()
          .then((data) => (serviceCaseId.current = data?.at(0)?.id))
          .then(() => {
            if (serviceCaseId.current && !isEqual(timeTrackerCaseData, lastCreatedTrackerData)) {
              startTracking();
            }
          });
      } else {
        if (!isEqual(timeTrackerCaseData, lastCreatedTrackerData)) {
          startTracking().then((ttData: any) => {
            setLastCreatedTrackerData(timeTrackerCaseData);
          });
        }
      }
    }
    // We do *not* want this to fire if lastCreatedTrackerData changes
    // (it will always change after we start a new tracker)

    // eslint-disable-next-line
  }, [
    application,
    component,
    caseId,
    validToken,
    trackingEnabled,
    isLauncher,
    launcherRequiresReviewId,
    reviewId,
    startTracking,
    searchServiceCases,
    serviceCaseId,
    queueIdRef,
  ]);

  useEffect(() => {
    // RW-747: keepAlive is gated too — if the flag flips off mid-session, further keepAlives stop.
    if (data?.id && trackingEnabled) {
      let interval = setInterval(() => {
        keepAlive();
      }, 30000);
      intervalId.current = interval;
      return () => {
        clearInterval(interval);
      };
    }
  }, [data?.id, trackingEnabled, keepAlive]);

  useEffect(() => {
    if (startTrackingError) {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
      console.error(
        `Error starting tracker for component ${component} in ${application}: ${startTrackingError.message}`
      );
    }
  }, [application, component, startTrackingError]);

  useEffect(() => {
    if (keepAliveError) {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
      console.error(
        `Error keeping tracker alive for component ${component} in ${application}: ${keepAliveError.message}`
      );
    }
  }, [application, component, keepAliveError]);

  useEffect(() => {
    if (data?.id && queueId !== queueIdRef) {
      stop("QUEUE_SWITCH").then(() => setQueueId(queueId));
    }
  }, [data?.id, queueId, queueIdRef, stop]);
  return null;
}
